import React from 'react';
import Modal from './Modal';
import Button from '../Button/Button';
import { AlertTriangle, AlertCircle, CheckCircle2, HelpCircle } from 'lucide-react';

/**
 * Modern Confirmation Dialog replacing legacy duplicated confirmation modals.
 */
const ConfirmDialog = ({
  isOpen = true,
  title = 'Confirm Action',
  description,
  error,
  confirmText = 'Confirm',
  ConfirmText, // backward compatibility
  cancelText = 'Cancel',
  variant = 'danger', // 'danger' | 'warning' | 'primary' | 'success'
  type, // alias for variant
  loading = false,
  onConfirm,
  handleConfirmation, // backward compatibility
  onClose,
  hideModal, // backward compatibility
}) => {
  const appliedVariant = type || variant;
  const appliedConfirmText = ConfirmText || confirmText;
  const handleConfirmAction = onConfirm || handleConfirmation;
  const handleCloseAction = onClose || hideModal;

  const iconConfig = {
    danger: {
      icon: <AlertCircle className="w-6 h-6 text-red-600" />,
      bg: 'bg-red-50',
      buttonVariant: 'danger',
    },
    warning: {
      icon: <AlertTriangle className="w-6 h-6 text-amber-600" />,
      bg: 'bg-amber-50',
      buttonVariant: 'primary',
    },
    primary: {
      icon: <HelpCircle className="w-6 h-6 text-primary" />,
      bg: 'bg-primary-soft',
      buttonVariant: 'primary',
    },
    success: {
      icon: <CheckCircle2 className="w-6 h-6 text-emerald-600" />,
      bg: 'bg-emerald-50',
      buttonVariant: 'primary',
    },
  };

  const currentConfig = iconConfig[appliedVariant] || iconConfig.primary;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseAction}
      loading={loading}
      size="sm"
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${currentConfig.bg}`}>
            {currentConfig.icon}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-stride-heading tracking-tight">
              {title}
            </h3>
            {description && (
              <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                {description}
              </p>
            )}
            {error && (
              <div className="mt-3 p-2.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600 font-medium">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={loading}
            onClick={() => handleCloseAction && handleCloseAction(false)}
          >
            {cancelText}
          </Button>

          <Button
            variant={currentConfig.buttonVariant}
            size="sm"
            loading={loading}
            onClick={() => handleConfirmAction && handleConfirmAction(true)}
          >
            {appliedConfirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;

